import { createSignal } from "solid-js";
import type { IntakeId, ProjectId } from "../api/types";

/**
 * The intake each project's Plan page was last working on, keyed by project.
 *
 * Kept outside any component so a planning session survives navigating away
 * from /plan and back — PlanPage is mounted per-route, so a local signal would
 * drop the thread the moment the user checked the backlog. Mirrored into
 * `sessionStorage` so a reload doesn't lose it either, and cleared with the
 * browser session (FLOW-547).
 */
const STORAGE_KEY = "flowicity_plan_sessions";

type Sessions = Record<ProjectId, IntakeId>;

function read(): Sessions {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Sessions) : {};
  } catch {
    return {};
  }
}

function write(sessions: Sessions): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  } catch {
    // Storage unavailable (private mode, quota) — in-memory state still works.
  }
}

const [sessions, setSessions] = createSignal<Sessions>(read());

export function activeIntakeId(projectId: ProjectId): IntakeId | undefined {
  return sessions()[projectId];
}

export function setActiveIntakeId(
  projectId: ProjectId,
  intakeId: IntakeId | null,
): void {
  setSessions((prev) => {
    const next = { ...prev };
    if (intakeId === null) {
      delete next[projectId];
    } else {
      next[projectId] = intakeId;
    }
    write(next);
    return next;
  });
}
